import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button, { buttonVariants } from "@react/shared/ui/Button"
import { getRequest, postRequest, patchRequest, pathname } from "@react/helpers/fetch"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { useEndpoints, useSetEndpoints } from "../context/EndpointsContext"
import { serializeTree } from "../helpers/serializer"
import DuplicateQuoteModal from "@react/quotes/project_quotes_index/components/DuplicateQuoteModal"
import CreateProjectModal  from "@react/quotes/project_quotes_index/components/CreateProjectModal"
import SendEmailModal      from "./SendEmailModal"
import LinkOpportunityModal from "./LinkOpportunityModal"
import FooterActionsMenu    from "./FooterActionsMenu"
import ValidationModal      from "@react/shared/validation/ValidationModal"
import Badge                from "@react/shared/ui/Badge"

const BuilderFooter = ({ backRoute, saveUrl, saveMethod, hideBackArrow, readOnly }) => {
  const { state, dispatch } = useQuoteEditor()
  const endpoints    = useEndpoints()
  const setEndpoints = useSetEndpoints()
  const [currentUrl,    setCurrentUrl]    = useState(saveUrl)
  const [currentMethod, setCurrentMethod] = useState(saveMethod)
  const [previewing,    setPreviewing]    = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [emailOpen,     setEmailOpen]     = useState(false)
  const [convertOpen,   setConvertOpen]   = useState(false)
  const [linkOpen,      setLinkOpen]      = useState(false)
  const [validationOpen, setValidationOpen] = useState(false)
  const lockedByValidation = useRef(false)
  const [busyVersion,   setBusyVersion]   = useState(false)
  const [busyStatus,    setBusyStatus]    = useState(false)
  const [busySaveTemplate, setBusySaveTemplate] = useState(false)

  const toIsoDate = value => {
    if (!value) return null
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (ymd) return value
    const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`
    return value
  }

  const buildPropertyFieldsAttributes = () => (state.propertyFields || []).map(f => ({
    id:                          f.id,
    property_field_blueprint_id: f.property_field_blueprint_id,
    title:                       f.title,
    field_type:                  f.field_type,
    order:                       f.order,
    string_answer:               f.string_answer,
    boolean_answer:              f.boolean_answer,
    date_answer:                 f.date_answer,
    integer_answer:              f.integer_answer,
    float_answer:                f.float_answer,
  }))

  const buildPayload = () => ({
    quote: {
      title:                      state.title,
      status:                     state.status,
      client_id:                  state.client?.id,
      site_id:                    state.site?.id,
      manager_id:                 state.managerId || state.manager?.id,
      partner_id:                 state.partnerId,
      contact_id:                 state.contact?.id,
      creation_date:              toIsoDate(state.creationDate),
      expiration_date:            toIsoDate(state.expirationDate),
      discount:                   state.discount,
      show_site_address:          !!state.showSiteAddress,
      show_service_components:    !!state.showServiceComponents,
      overhead_coef_overrides:    state.overheadCoefOverrides || {},
      sale_coef_overrides:        state.saleCoefOverrides || {},
      qlines_attributes:          serializeTree(state.sections, state.deletedItems),
      property_fields_attributes: buildPropertyFieldsAttributes(),
    },
  })

  const findLeafMissingVat = nodes => {
    for (const node of nodes || []) {
      if (!node || node._destroy) continue
      const liveChildren = (node.children || []).filter(c => c && !c._destroy)
      const hasServices  = liveChildren.some(c => c.type === "service")
      if (hasServices) {
        if (!node.vat_rate?.id) return node
        continue
      }
      const nested = findLeafMissingVat(liveChildren)
      if (nested) return nested
    }
    return null
  }

  const reportMissingVat = leaf => {
    const row = document.querySelector(`[data-uid="${leaf._uid}"]`)
    const select = row?.querySelector("select[data-vat-required]")
    if (row) row.scrollIntoView({ block: "center", behavior: "smooth" })
    if (select?.reportValidity) {
      select.reportValidity()
    } else {
      flashAlert(I18n.t("quotes.builder.footer.vat_required"))
    }
  }

  const missingRequiredFields = () => {
    const missing = []
    ;(state.propertyFields || []).forEach(f => {
      if (!f.required || f.field_type === "sub_title") return
      let filled = false
      switch (f.field_type) {
        case "boolean":  filled = f.boolean_answer === true; break
        case "date":     filled = !!f.date_answer; break
        case "integer":  filled = f.integer_answer != null; break
        case "float":    filled = f.float_answer != null; break
        default:         filled = !!(f.string_answer && String(f.string_answer).trim())
      }
      if (!filled) missing.push(f.title)
    })
    return missing
  }

  const submit = exit => {
    if (state.saving) return

    const managerId = state.managerId || state.manager?.id
    if (!managerId && !state.isTemplate) {
      flashAlert(I18n.t("quotes.builder.footer.manager_required"))
      dispatch({ type: "SET_TAB", payload: "settings" })
      return
    }

    if (state.newRecord && (state.partners || []).length > 0 && !state.partnerId && !state.isTemplate) {
      flashAlert(I18n.t("quotes.builder.footer.partner_required"))
      dispatch({ type: "SET_TAB", payload: "settings" })
      return
    }

    const missing = missingRequiredFields()
    if (missing.length > 0) {
      flashAlert(I18n.t("quotes.builder.footer.missing_required_fields", { fields: missing.join(", ") }))
      dispatch({ type: "SET_TAB", payload: "settings" })
      return
    }

    const leafMissingVat = findLeafMissingVat(state.sections)
    if (leafMissingVat) {
      reportMissingVat(leafMissingVat)
      return
    }

    dispatch({ type: "SET_SAVING", payload: true })

    const url     = pathname({ path: currentUrl, params: exit ? { exit: 1, back_route: backRoute } : {} })
    const request = currentMethod === "patch" ? patchRequest : postRequest

    request({ url, body: buildPayload() })
      .then(response => {
        if (!response?.success) return saveFailed(response?.errors)
        if (response.pending) return pollSave(response.status_url, Date.now())
        applySaved(response)
      })
      .catch(() => {
        dispatch({ type: "SET_SAVING", payload: false })
        flashAlert(I18n.t("quotes.builder.footer.network_error"))
      })
  }

  const SAVE_POLL_INTERVAL_MS = 1500
  const SAVE_TIMEOUT_MS       = 10 * 60 * 1000

  const saveFailed = errors => {
    dispatch({ type: "SET_SAVING", payload: false })
    const list = errors?.length ? errors : [I18n.t("quotes.builder.footer.unknown_error")]
    flashAlert(Array.isArray(list) ? list.join(", ") : String(list))
  }

  // A dropped poll is retried: the save goes on server side.
  const pollSave = (statusUrl, startedAt) => {
    if (Date.now() - startedAt > SAVE_TIMEOUT_MS) return saveFailed([I18n.t("quotes.builder.footer.save_timeout")])

    const again = () => setTimeout(() => pollSave(statusUrl, startedAt), SAVE_POLL_INTERVAL_MS)
    getRequest(statusUrl)
      .then(response => {
        if (response?.status === "done")   return applySaved(response)
        if (response?.status === "failed") return saveFailed(response.errors)
        again()
      })
      .catch(again)
  }

  const applySaved = response => {
    if (response.notice) flashNotice(response.notice)
    if (response.next_url)    setCurrentUrl(response.next_url)
    if (response.next_method) setCurrentMethod(response.next_method)
    if (response.endpoints)   setEndpoints(current => ({ ...current, ...response.endpoints }))
    if (response.edit_url && window.history?.replaceState) {
      window.history.replaceState({}, "", response.edit_url)
    }
    if (response.data) {
      dispatch({ type: "RESET_FROM_SERVER", payload: response.data })
      dispatch({ type: "SET_SAVING", payload: false })
    } else if (response.redirect_to) {
      window.location.href = response.redirect_to
    } else {
      dispatch({ type: "MARK_CLEAN" })
      dispatch({ type: "SET_SAVING", payload: false })
    }
  }

  const PREVIEW_TIMEOUT_MS  = 60000
  const PREVIEW_POLL_INTERVAL_MS = 2000

  const previewPdf = () => {
    if (previewing || state.saving) return
    const leafMissingVat = findLeafMissingVat(state.sections)
    if (leafMissingVat) {
      reportMissingVat(leafMissingVat)
      return
    }
    setPreviewing(true)
    // Opened on the click: once the PDF is ready it is no longer a user gesture, and the browser blocks it.
    const previewWindow = window.open("", "_blank")
    const url     = pathname({ path: currentUrl, params: { preview: 1 } })
    const request = currentMethod === "patch" ? patchRequest : postRequest
    request({ url, body: buildPayload() })
      .then(response => {
        if (!response?.status_url) {
          previewFailed(previewWindow, "quotes.builder.footer.pdf_error")
          return
        }
        pollPreview(response.status_url, Date.now(), previewWindow)
      })
      .catch(() => previewFailed(previewWindow, "quotes.builder.footer.network_error"))
  }

  const previewFailed = (previewWindow, messageKey) => {
    previewWindow?.close()
    flashAlert(I18n.t(messageKey))
    setPreviewing(false)
  }

  const pollPreview = (statusUrl, startedAt, previewWindow) => {
    if (Date.now() - startedAt > PREVIEW_TIMEOUT_MS) {
      previewFailed(previewWindow, "quotes.builder.footer.pdf_error")
      return
    }
    fetch(statusUrl, { credentials: "same-origin" })
      .then(res => {
        if (res.status === 200) {
          return res.blob().then(blob => {
            const url = URL.createObjectURL(blob)
            if (previewWindow && !previewWindow.closed) previewWindow.location.href = url
            else window.open(url, "_blank")
            setPreviewing(false)
          })
        }
        if (res.status === 202) {
          setTimeout(() => pollPreview(statusUrl, startedAt, previewWindow), PREVIEW_POLL_INTERVAL_MS)
          return
        }
        previewFailed(previewWindow, "quotes.builder.footer.pdf_error")
      })
      .catch(() => previewFailed(previewWindow, "quotes.builder.footer.network_error"))
  }

  const saving = state.saving
  const dirty  = state.dirty

  // A read-only quote can never save its edits, so they must not block its copy.
  const canDuplicate  = !!(state.quoteId && endpoints.duplicate_url && (readOnly || !dirty))
  const canNewVersion = !!(state.quoteId && endpoints.new_version_url && !dirty)
  const canSendEmail  = !!(state.quoteId && endpoints.send_quote_url && endpoints.can_send_email && !dirty)
  const canSetStatus  = !!(state.quoteId && endpoints.set_status_url && !dirty)
  const canConvert    = !!(state.quoteId && endpoints.convert_to_project_url && state.canConvertToProject && !dirty)

  const handleStatusChange = nextStatus => {
    if (!canSetStatus || busyStatus) return
    setBusyStatus(true)
    const url = pathname({ path: endpoints.set_status_url, params: { "quote[status]": nextStatus } })
    patchRequest({ url })
      .then(response => {
        if (!response || response.errors || response.success === false) {
          const errors = response?.errors || [I18n.t("quotes.builder.footer.unknown_error")]
          flashAlert(Array.isArray(errors) ? errors.join(", ") : String(errors))
          return
        }
        if (response.data) {
          dispatch({ type: "RESET_FROM_SERVER", payload: response.data })
        } else {
          dispatch({ type: "SET_QUOTE_FIELD", payload: { field: "status", value: response.status || nextStatus } })
          dispatch({ type: "MARK_CLEAN" })
        }
        flashNotice(I18n.t("quotes.status.change_success"))
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setBusyStatus(false))
  }

  const handleNewVersion = () => {
    if (!canNewVersion || busyVersion) return
    setBusyVersion(true)
    postRequest({ url: endpoints.new_version_url })
      .then(response => {
        if (response?.errors) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        if (response?.id) window.location.href = `/quotes/${response.id}/edit`
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setBusyVersion(false))
  }

  const submitDuplicate = values => {
    const params = {
      "quote[title]":     values.title,
      "quote[client_id]": values.client_id,
      "quote[site_id]":   values.site_id,
    }
    return postRequest({ url: pathname({ path: endpoints.duplicate_url, params }) })
      .then(response => {
        if (response?.errors) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        if (response?.id) window.location.href = `/quotes/${response.id}/edit`
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
  }

  const canSaveAsTemplate = !!(state.quoteId && endpoints.save_as_template_url && !state.isTemplate)

  const handleSaveAsTemplate = () => {
    if (!canSaveAsTemplate || dirty || busySaveTemplate) return
    setBusySaveTemplate(true)
    postRequest({ url: endpoints.save_as_template_url })
      .then(response => {
        if (!response?.success) {
          const errors = response?.errors || [I18n.t("quotes.builder.footer.unknown_error")]
          flashAlert(Array.isArray(errors) ? errors.join(", ") : String(errors))
          return
        }
        flashNotice(response.notice || I18n.t("quotes.project_templates.save_as_template.success"))
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setBusySaveTemplate(false))
  }

  const duplicateSourceQuote = {
    number:                  state.number,
    default_duplicate_title: state.title ? `${state.title}${I18n.t("quotes.duplicate.copy_suffix")}` : "",
    title:                   state.title,
    client:                  state.client,
    site:                    state.site,
  }

  useEffect(() => {
    if (!dirty || readOnly) return undefined
    const handler = e => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [dirty, readOnly])

  // Unlike undo, this shortcut also applies inside an input field, where it is
  // used; without it the browser offers to save the web page.
  useEffect(() => {
    if (readOnly) return undefined
    const onKey = event => {
      if (event.key?.toLowerCase() !== "s") return
      if (!(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      if (!dirty || saving) return
      submit(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  })

  const duplicateButton = (
    <Button variant="ghost" size="sm" onClick={() => setDuplicateOpen(true)} disabled={!canDuplicate}>
      <FontAwesomeIcon icon="copy" />
      {I18n.t("button.duplicate")}
    </Button>
  )

  const duplicateModal = (
    <DuplicateQuoteModal
      isOpen                 = {duplicateOpen}
      quote                  = {duplicateSourceQuote}
      onClose                = {() => setDuplicateOpen(false)}
      onSubmit               = {submitDuplicate}
      clientsAutocompleteUrl = {endpoints.clients_autocomplete}
      sitesAutocompleteUrl   = {endpoints.sites_autocomplete}
    />
  )

  // A read-only quote cannot send its tree for a preview, so it prints what is saved.
  const savedPdfButton = !state.isTemplate && endpoints.pdf_url && (
    <a
      href      = {endpoints.pdf_url}
      target    = "_blank"
      rel       = "noopener noreferrer"
      className = {buttonVariants({ variant: "ghost", size: "sm" })}
    >
      <FontAwesomeIcon icon="file-pdf" />
      {I18n.t("button.pdf")}
    </a>
  )

  const awaitingApproval = !!endpoints.validation_badge && !["approved", "not_applicable"].includes(endpoints.validation_badge.status)

  // Reachable on a locked quote too: its approval history explains the lock.
  const validationButton = !state.isTemplate && state.quoteId && endpoints.validation_url ? (
    <Button
      variant = "ghost"
      size    = "sm"
      onClick = {() => setValidationOpen(true)}
      title   = {I18n.t("validation.workflow.modal_title")}
    >
      <FontAwesomeIcon icon="user-check" />
      {endpoints.validation_badge && (
        <Badge tone={endpoints.validation_badge.tone} variant="soft" size="xs">{endpoints.validation_badge.label}</Badge>
      )}
    </Button>
  ) : null

  // An approved or rejected quote can no longer be opened (QuotePolicy#edit?): back to the list.
  const closeValidation = () => {
    setValidationOpen(false)
    if (lockedByValidation.current) window.location.href = backRoute || "/quotes?type=projects"
  }

  const validationModal = endpoints.validation_url ? (
    <ValidationModal
      isOpen   = {validationOpen}
      onClose  = {closeValidation}
      url      = {endpoints.validation_url}
      launchBlockedReason = {dirty ? I18n.t("quotes.project_templates.save_as_template.save_first") : null}
      onChange = {validation => {
        if (!readOnly && validation.can_edit === false) lockedByValidation.current = true
        setEndpoints(current => ({
          ...current,
          validation_badge: validation.badge,
          ...(validation.can_send == null ? {} : { can_send_email: validation.can_send }),
        }))
      }}
    />
  ) : null

  if (readOnly) {
    if (!canDuplicate && !savedPdfButton && !validationButton) return null
    return (
      <div className="flex flex-wrap items-center justify-end gap-2.5 [&>*]:whitespace-nowrap">
        {validationButton}
        {canDuplicate && duplicateButton}
        {savedPdfButton}
        {canDuplicate && duplicateModal}
        {validationModal}
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-end gap-2.5 [&>*]:whitespace-nowrap">
        {!hideBackArrow && (
          <a
            href={backRoute || "/quotes"}
            className={buttonVariants({ variant: "secondary", size: "sm" })}
            title={I18n.t("quotes.builder.footer.cancel")}
          >
            <FontAwesomeIcon icon="arrow-left" />
          </a>
        )}
        {validationButton}
        {!state.isTemplate && canSendEmail !== undefined && state.quoteId && (
          <>
            <Button
              variant  = "ghost"
              size     = "sm"
              onClick  = {() => setEmailOpen(true)}
              disabled = {!canSendEmail}
              title    = {
                !canSendEmail && dirty ? I18n.t("quotes.builder.meta.save_before_email") :
                !canSendEmail && awaitingApproval ? I18n.t("validation.workflow.approval_required") : ""
              }
            >
              <FontAwesomeIcon icon="paper-plane" />
              {I18n.t("quotes.bigcard.send")}
            </Button>
            {canSetStatus && state.status !== "Sent" && state.status !== "Accepted" && state.status !== "Rejected" && state.status !== "Invoiced" && state.status !== "Partially invoiced" && (
              <Button variant="ghost" size="sm" onClick={() => handleStatusChange("Sent")}>
                <FontAwesomeIcon icon="paper-plane" />
                {I18n.t("quotes.builder.status_menu.mark_as_sent")}
              </Button>
            )}
            {canSetStatus && state.status === "Sent" && (
              <>
                <Button variant="ghost" size="sm" onClick={() => handleStatusChange("Accepted")}>
                  <FontAwesomeIcon icon="circle-check" />
                  {I18n.t("quotes.builder.status_menu.mark_as_accepted")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleStatusChange("Rejected")}>
                  <FontAwesomeIcon icon="circle-xmark" />
                  {I18n.t("quotes.builder.status_menu.mark_as_rejected")}
                </Button>
              </>
            )}
            {duplicateButton}
            {canConvert && (
              <Button variant="ghost" size="sm" onClick={() => setConvertOpen(true)}>
                <FontAwesomeIcon icon="hard-hat" />
                {I18n.t("quotes.bigcard.convert_to_project")}
              </Button>
            )}
            {endpoints.view_project_url && (
              <a
                href      = {endpoints.view_project_url}
                className = {buttonVariants({ variant: "ghost", size: "sm" })}
              >
                <FontAwesomeIcon icon="hard-hat" />
                {I18n.t("quotes.bigcard.view_project")}
              </a>
            )}
          </>
        )}
        <FooterActionsMenu items={[
          !state.isTemplate && state.quoteId && endpoints.new_version_url && {
            key:      "new_version",
            icon:     "code-branch",
            label:    I18n.t("quotes.bigcard.new_version"),
            busy:     busyVersion,
            disabled: !canNewVersion || busyVersion,
            title:    dirty ? I18n.t("quotes.project_templates.save_as_template.save_first") : "",
            onClick:  handleNewVersion,
          },
          !state.isTemplate && endpoints.link_opportunity_url && {
            key:     "link_opportunity",
            icon:    "link",
            label:   endpoints.linked_opportunity
                       ? I18n.t("quotes.bigcard.linked_opportunity")
                       : I18n.t("button.link"),
            onClick: () => setLinkOpen(true),
          },
          canSaveAsTemplate && {
            key:      "save_as_template",
            icon:     "bookmark",
            label:    I18n.t("quotes.project_templates.save_as_template.button"),
            busy:     busySaveTemplate,
            disabled: busySaveTemplate || dirty,
            title:    dirty ? I18n.t("quotes.project_templates.save_as_template.save_first") : "",
            onClick:  handleSaveAsTemplate,
          },
        ]} />
        {!state.isTemplate && (
          <Button variant="ghost" size="sm" onClick={previewPdf} disabled={saving || previewing}>
            <FontAwesomeIcon icon={previewing ? "spinner" : "file-pdf"} spin={previewing} />
            {previewing
              ? I18n.t("quotes.builder.footer.generating")
              : I18n.t("quotes.builder.footer.preview_pdf")}
          </Button>
        )}
        {/* Un bouton éteint qui ne dit pas pourquoi se lit comme une panne. Il
            n'y a rien à enregistrer : autant l'annoncer, et se féliciter. */}
        <Button
          variant  = "primary"
          size     = "sm"
          onClick  = {() => submit(false)}
          disabled = {saving || !dirty}
          title    = {dirty ? "" : I18n.t("quotes.builder.footer.saved_hint")}
        >
          <FontAwesomeIcon icon={dirty ? "floppy-disk" : "check"} />
          {dirty
            ? I18n.t("quotes.builder.footer.save")
            : I18n.t("quotes.builder.footer.saved")}
        </Button>
      </div>

      {(saving || previewing || busyStatus) && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl bg-white border border-slate-200 shadow-lg">
            <FontAwesomeIcon icon="spinner" spin className="text-brand-500 text-lg" />
            <span className="text-sm font-medium text-slate-900">
              {saving
                ? I18n.t("quotes.builder.footer.saving")
                : previewing
                  ? I18n.t("quotes.builder.footer.generating_pdf")
                  : I18n.t("quotes.builder.footer.updating_status")}
            </span>
          </div>
        </div>
      )}

      {duplicateModal}

      {validationModal}

      <SendEmailModal
        isOpen   = {emailOpen}
        onClose  = {() => setEmailOpen(false)}
        sendUrl  = {endpoints.send_quote_url}
        defaults = {endpoints.email_defaults || { recipient: endpoints.default_email || "" }}
      />

      <CreateProjectModal
        isOpen           = {convertOpen}
        onClose          = {() => setConvertOpen(false)}
        eyebrow          = {state.number}
        convertUrl       = {endpoints.convert_to_project_url}
        managers         = {state.managers}
        currentUserId    = {endpoints.current_user_id}
        defaultStartDate = {toIsoDate(state.creationDate)}
      />

      {endpoints.link_opportunity_url && (
        <LinkOpportunityModal
          isOpen        = {linkOpen}
          onClose       = {() => setLinkOpen(false)}
          linkUrl       = {endpoints.link_opportunity_url}
          unlinkUrl     = {endpoints.unlink_opportunity_url}
          linked        = {endpoints.linked_opportunity}
          opportunities = {endpoints.pipeline_opportunities || []}
        />
      )}
    </>
  )
}

export default BuilderFooter
